// SPDX-License-Identifier: BUSL-1.1

/**
 * Natural-language activity feed — replaces DataTable on dashboard for recent audit entries.
 *
 */

import type * as React from 'react';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ActorAvatar } from '@/components/actor-avatar';
import { ResourcePill } from '@/components/resource-pill';
import { formatAuditAction } from '@/lib/format-audit';
import { relativeTime } from '@/lib/format';
import type { AuditEntry } from '@/types/api';

interface ActivityFeedProps {
  entries: AuditEntry[];
  /** Maximum number of entries to render. Default 8. */
  maxItems?: number;
  /** URL to navigate to on "View all →". Default '/audit'. */
  viewAllHref?: string;
}

export function ActivityFeed({
  entries,
  maxItems = 8,
  viewAllHref = '/audit',
}: ActivityFeedProps): React.ReactElement {
  const visible = entries.slice(0, maxItems);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Recent Activity</CardTitle>
        <Link
          to={viewAllHref}
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          View all →
        </Link>
      </CardHeader>
      <CardContent className="p-0">
        {visible.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-neutral-500">
            No recent activity.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-800">
            {visible.map((entry) => (
              <ActivityItem key={entry.id} entry={entry} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityItem({ entry }: { entry: AuditEntry }): React.ReactElement {
  const { verb, resource, variant, suffix } = formatAuditAction(entry);
  // Relative time ("just now", "12m ago", "3h ago", "2d ago") gives date
  // context for entries spanning multiple days. Full ISO stays in the
  // `dateTime` attribute + `title` for precise hover-to-inspect.
  const timeLabel = relativeTime(entry.createdAt);
  const fullTimestamp = new Date(entry.createdAt).toLocaleString();

  return (
    <li className="flex items-start gap-3 px-6 py-3 transition hover:bg-neutral-900/50">
      <time
        className="w-14 shrink-0 pt-0.5 font-mono text-xs text-neutral-500"
        dateTime={entry.createdAt}
        title={fullTimestamp}
      >
        {timeLabel}
      </time>
      <ActorAvatar
        id={entry.actorId}
        size="sm"
        system={entry.actorType === 'system'}
      />
      <p className="flex-1 text-sm text-neutral-400">
        <span className="text-neutral-300">{entry.actorId}</span>{' '}
        {verb}{' '}
        <ResourcePill variant={variant}>{resource}</ResourcePill>
        {suffix ? ` ${suffix}` : null}
      </p>
    </li>
  );
}
